import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { useNotifications } from '../../contexts/NotificationContext';
import { format } from 'date-fns';

const NotificationCenter = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, archiveNotification, loading } = useNotifications();
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    let filtered = [...notifications];

    if (filterType !== 'all') {
      filtered = filtered?.filter(n => n?.type === filterType);
    }

    if (filterSeverity !== 'all') {
      filtered = filtered?.filter(n => n?.severity === filterSeverity);
    }

    if (filterStatus !== 'all') {
      filtered = filtered?.filter(n => n?.status === filterStatus);
    }

    setFilteredNotifications(filtered);
  }, [notifications, filterType, filterSeverity, filterStatus]);

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-error/10 text-error border-error',
      high: 'bg-warning/10 text-warning border-warning',
      medium: 'bg-accent/10 text-accent border-accent',
      low: 'bg-info/10 text-info border-info',
      info: 'bg-secondary/10 text-secondary border-secondary',
    };
    return colors?.[severity] || colors?.info;
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      critical: 'AlertTriangle',
      high: 'AlertCircle',
      medium: 'Info',
      low: 'Bell',
      info: 'MessageCircle',
    };
    return icons?.[severity] || 'Bell';
  };

  const getTypeLabel = (type) => {
    const labels = {
      fraud_alert: 'Fraud Alert',
      settlement_issue: 'Settlement Issue',
      system_update: 'System Update',
      user_activity: 'User Activity',
      transaction_alert: 'Transaction Alert',
    };
    return labels?.[type] || type;
  };

  const handleNotificationClick = (notification) => {
    if (notification?.status === 'unread') {
      markAsRead(notification?.id);
    }
  };

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'fraud_alert', label: 'Fraud Alerts' },
    { value: 'settlement_issue', label: 'Settlement Issues' },
    { value: 'transaction_alert', label: 'Transaction Alerts' },
    { value: 'system_update', label: 'System Updates' },
    { value: 'user_activity', label: 'User Activity' },
  ];

  const severityOptions = [
    { value: 'all', label: 'All Severities' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
    { value: 'info', label: 'Info' },
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'unread', label: 'Unread' },
    { value: 'read', label: 'Read' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Header Section */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Notification Center
              </h1>
              <p className="text-sm text-muted-foreground">
                {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
              </p>
            </div>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                iconName="CheckCheck"
                iconPosition="left"
                onClick={markAllAsRead}
              >
                Mark All as Read
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card rounded-xl border border-border p-4 md:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Type"
              options={typeOptions}
              value={filterType}
              onChange={setFilterType}
            />
            <Select
              label="Severity"
              options={severityOptions}
              value={filterSeverity}
              onChange={setFilterSeverity}
            />
            <Select
              label="Status"
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
            />
          </div>
        </div>

        {/* Notifications List */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-card rounded-xl border border-border p-8 text-center">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading notifications...</p>
            </div>
          ) : filteredNotifications?.length === 0 ? (
            <div className="bg-card rounded-xl border border-border p-8 text-center">
              <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No notifications found</h3>
              <p className="text-sm text-muted-foreground">
                {filterType !== 'all' || filterSeverity !== 'all' || filterStatus !== 'all' ?'Try adjusting your filters' :'You\'re all caught up!'}
              </p>
            </div>
          ) : (
            filteredNotifications?.map((notification) => (
              <div
                key={notification?.id}
                className={`bg-card rounded-xl border-2 p-4 md:p-6 transition-all hover:shadow-md cursor-pointer ${
                  notification?.status === 'unread' ? 'border-primary bg-primary/5' : 'border-border'
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0 border-2 ${
                    getSeverityColor(notification?.severity)
                  }`}>
                    <Icon name={getSeverityIcon(notification?.severity)} size={20} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-base md:text-lg font-semibold text-foreground">
                            {notification?.title}
                          </h3>
                          {notification?.status === 'unread' && (
                            <span className="w-2 h-2 bg-primary rounded-full"></span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                            getSeverityColor(notification?.severity)
                          }`}>
                            {notification?.severity?.toUpperCase()}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {getTypeLabel(notification?.type)}
                          </span>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {format(new Date(notification?.createdAt), 'MMM d, h:mm a')}
                      </span>
                    </div>
                    <p className="text-sm text-foreground/80 mb-4">
                      {notification?.message}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {notification?.status === 'unread' && (
                        <Button
                          size="sm"
                          variant="outline"
                          iconName="Check"
                          iconPosition="left"
                          onClick={(e) => {
                            e?.stopPropagation();
                            markAsRead(notification?.id);
                          }}
                        >
                          Mark as Read
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        iconName="Archive"
                        iconPosition="left"
                        onClick={(e) => {
                          e?.stopPropagation();
                          archiveNotification(notification?.id);
                        }}
                      >
                        Archive
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;